import { Box, DrawerProps, DrawerSlotsAndSlotProps, ModalClose, Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { SlotCommonProps } from '@mui/joy/utils/types';
import { isString } from 'lodash';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { Position } from 'core/enums';
import { useNotificationContext } from 'core/states/notification';
import { renderHtmlMessage } from 'core/utils';
import { Color, Direction, Level, Size } from 'theme/enums';

import { Content, Drawer, DRAWER_GUTTER, Title } from './style';

interface IProps extends Omit<DrawerProps, 'open' | 'title'> {
  isOpen: boolean;
  onClosePanel: () => void;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  extra?: ReactNode | ReactNode[];
  buttons?: ReactNode;
  children?: ReactNode | ReactNode[];
  openFrom?: Position;
  size?: Size;
  autoWidth?: boolean;
  fullScreen?: boolean;
  drawerSlotAndSlotProps?: DrawerSlotsAndSlotProps;
  contentStyle?: SxProps;
  inheritContentWidth?: boolean;
  interactive?: boolean;
  disableAutoClose?: boolean;
  disableClose?: boolean;
  // A panel that is not part of the page's flow (the bell's notifications) leaves the page's
  // form and drawer banners in place when it closes.
  keepPageNotificationsOnClose?: boolean;
  level?: number;
}

export const SlidingPanel = ({
  isOpen,
  onClosePanel,
  title,
  subtitle,
  extra,
  children,
  buttons,
  openFrom = Position.RIGHT,
  size,
  autoWidth = false,
  fullScreen = false,
  drawerSlotAndSlotProps: defaultDrawerSlotAndSlotProps,
  contentStyle: customContentStyle,
  inheritContentWidth = false,
  interactive = false,
  disableAutoClose = false,
  disableClose = false,
  keepPageNotificationsOnClose = false,
  level = 0,
  ...rest
}: IProps) => {
  const [contentWidth, setContentWidth] = useState<number | undefined>();

  const contentRef = useRef<HTMLDivElement>(null);

  const { clearDrawerNotification, clearFormNotification } = useNotificationContext();

  useEffect(() => {
    if (!autoWidth || !isOpen) {
      return;
    }

    // Small delay to ensure DOM is ready for nested sliding panels
    const timer = setTimeout(() => {
      if (!contentRef.current) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContentWidth(entry.contentRect.width);
        }
      });

      observer.observe(contentRef.current);

      return () => observer.disconnect();
    }, 0);

    return () => clearTimeout(timer);
  }, [autoWidth, isOpen]);

  const handleOnClosePanel = (_, reason: string) => {
    if (disableAutoClose && ['backdropClick', 'escapeKeyDown'].includes(reason)) {
      return;
    }

    if (!keepPageNotificationsOnClose) {
      clearFormNotification();
      clearDrawerNotification();
    }

    onClosePanel();
  };

  const drawerSlotAndSlotProps = useMemo(() => {
    if (level === 0) {
      return defaultDrawerSlotAndSlotProps;
    }

    const slotProps =
      defaultDrawerSlotAndSlotProps?.slotProps as DrawerSlotsAndSlotProps['slotProps'];

    const contentSlotProps = slotProps?.content as SlotCommonProps;

    return {
      ...defaultDrawerSlotAndSlotProps,
      slotProps: {
        ...slotProps,
        content: {
          ...contentSlotProps,
          sx: {
            ...contentSlotProps?.sx,
            width: `min(100vw, var(--Drawer-horizontalSize) - ${level * 20}px)`,
          },
        },
      },
    };
  }, [defaultDrawerSlotAndSlotProps, level]);

  const hasButtons = Boolean(buttons);

  const contentStyle = useMemo(
    () => ({
      // A shrunk Content lets tall children run under the sticky buttons, out of scroll
      // reach, so a button-bar drawer defaults to natural height. Placed before
      // customContentStyle so a drawer whose content scrolls inside the panel (a grid or
      // list that relies on the Content `minHeight: 0` shrink from #1353) can opt back out
      // with contentStyle={{ minHeight: 0 }}.
      ...(hasButtons && {
        minHeight: 'auto',
      }),
      ...customContentStyle,
      ...(inheritContentWidth && {
        width: 'fit-content',
      }),
      backgroundColor: 'inherit',
    }),
    [customContentStyle, inheritContentWidth, hasButtons]
  );

  const subtitleContent = isString(subtitle) ? (
    <Typography color={Color.NEUTRAL} level={Level.TITLE_SM} component="div">
      {renderHtmlMessage(subtitle)}
    </Typography>
  ) : (
    subtitle
  );

  const panelContent = (
    <>
      <Stack
        spacing={1}
        sx={{
          position: 'sticky',
          backgroundColor: 'inherit',
          top: 0,
          zIndex: 999,
          marginLeft: `-${DRAWER_GUTTER}`,
          marginRight: `-${DRAWER_GUTTER}`,
          padding: '27px 24px 24px',
          marginBottom: '8px',
          boxShadow:
            '0px 2px 8px -2px rgba(21, 21, 21, 0.08), 0px 6px 12px -2px rgba(21, 21, 21, 0.08)',
          ...(inheritContentWidth &&
            contentWidth && {
              width: `calc(${contentWidth}px + 2 * ${DRAWER_GUTTER})`,
            }),
        }}
      >
        {title && (
          <Stack
            direction={Direction.ROW}
            sx={{ justifyContent: 'space-between', alignItems: 'center', minHeight: 32 }}
          >
            {isString(title) ? (
              <Title level={Level.H3} sx={{ overflowWrap: 'anywhere', paddingRight: '32px' }}>
                {renderHtmlMessage(title)}
              </Title>
            ) : (
              <Stack direction={Direction.ROW} gap={0.5}>
                {title}
              </Stack>
            )}
            <ModalClose disabled={disableClose} style={{ marginRight: '10px' }} />
          </Stack>
        )}
        {subtitle && subtitleContent}
        {extra}
      </Stack>

      {isOpen && (
        <Content ref={contentRef} sx={contentStyle}>
          {children}
        </Content>
      )}

      {buttons && (
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.background.surface,
            display: 'flex',
            padding: '15px 24px 24px',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 999,
            marginTop: '24px',
            marginLeft: `-${DRAWER_GUTTER}`,
            marginRight: `-${DRAWER_GUTTER}`,
            bottom: 0,
            width: `calc(100% + 2 * ${DRAWER_GUTTER})`,
            position: 'sticky',
          }}
        >
          {buttons}
        </Box>
      )}
    </>
  );

  return (
    <Drawer
      anchor={openFrom}
      open={isOpen}
      onClose={handleOnClosePanel}
      size={size}
      autoWidth={autoWidth}
      fullScreen={fullScreen}
      hideBackdrop={interactive}
      sx={interactive ? { width: 0 } : {}}
      slots={drawerSlotAndSlotProps?.slots}
      slotProps={drawerSlotAndSlotProps?.slotProps}
      {...rest}
    >
      {panelContent}
    </Drawer>
  );
};
